import { useEffect } from 'react'
import { Box, Button, IconButton, LinearProgress, Modal, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { motion, useAnimation } from 'framer-motion'
import { ReactComponent as WheelLg } from '../../assets/animation/WheelLg.svg'
import { ReactComponent as WheelSm } from '../../assets/animation/WheelSm.svg'
import { ReactComponent as FairManSuccess } from '../../assets/animation/FairManSuccess.svg'
import { ReactComponent as FairManUnsuccess } from '../../assets/animation/FairManUnsuccess.svg'
import { ReactComponent as ReportOutline } from '../../assets/animation/ReportOutline.svg'
import { ReactComponent as ReportBody } from '../../assets/animation/ReportBody.svg'
import { ReactComponent as ReportHeader } from '../../assets/animation/ReportHeader.svg'
import { ReactComponent as ReportSuccess } from '../../assets/animation/ReportSuccess.svg'
import { ReactComponent as ReportError } from '../../assets/animation/ReportError.svg'
import { fsColors } from '../../shared/constants'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import '../../App.css'
import { setReports } from '../../store/actions'

const FsReportSpinner = ({
  open,
  onClose,
  generationStatus,
}: {
  open: boolean
  onClose: (isError: boolean) => void
  generationStatus: string
}) => {
  const { t } = useTranslation()
  const { currentTheme } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()

  const outlineControls = useAnimation()
  const headerControls = useAnimation()
  const bodyControls = [useAnimation(), useAnimation(), useAnimation(), useAnimation()]
  const successControls = useAnimation()
  const failedControls = useAnimation()

  useEffect(() => {
    const playAnimation = async () => {
      await outlineControls.start({
        y: '0%',
        opacity: 1,
        transition: { duration: 1, ease: 'easeInOut' },
      })

      await headerControls.start({
        x: '0%',
        opacity: 1,
        transition: { duration: 1, ease: 'easeInOut' },
      })

      await bodyControls[0].start({
        x: '0%',
        opacity: 1,
        transition: { duration: 0.4, ease: 'easeInOut' },
      })
      await bodyControls[1].start({
        x: '0%',
        opacity: 1,
        transition: { duration: 0.3, ease: 'easeInOut' },
      })
      await bodyControls[2].start({
        x: '0%',
        opacity: 1,
        transition: { duration: 0.2, ease: 'easeInOut' },
      })
      await bodyControls[3].start({
        x: '0%',
        opacity: 1,
        transition: { duration: 0.1, ease: 'easeInOut' },
      })

      if (generationStatus === 'success') {
        await successControls.start({
          opacity: 1,
          rotate: 360,
          transition: { duration: 1, ease: 'easeInOut' },
        })
        await successControls.start({
          opacity: 1,
          transition: { duration: 1, ease: 'easeInOut', delay: 1 },
        })
      } else if (generationStatus === 'failed') {
        await failedControls.start({
          opacity: 1,
          scale: 1,
          transition: {
            duration: 1,
            ease: [0.175, 0.885, 0.32, 1.275],
            delay: 0.7,
          },
        })
      }
    }

    if (generationStatus === 'success' || generationStatus === 'failed') {
      playAnimation()
    }
  }, [generationStatus])

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason && reason === 'backdropClick') return
        onClose(false)
      }}
      disableEscapeKeyDown
    >
      <Box className="spinner-container">
        <Stack
          sx={{
            width: 450,
            height: 350,
            flexDirection: 'column',
            alignItems: 'flex-end',
            background: currentTheme === 'light' ? '#fff' : fsColors.fsGray.colors[0],
            borderRadius: 2,
            boxShadow: 4,
          }}
        >
          <IconButton
            onClick={() => {
              onClose(true)
            }}
          >
            <CloseIcon />
          </IconButton>
          <Stack
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              gap: 1,
            }}
          >
            {generationStatus === 'loading' ? (
              <>
                <WheelLg className="spin-animation " />
                <WheelSm className="reverse-spin-animation" />
                <FairManSuccess />
                <Typography variant="h6" color={fsColors.fsDarkBlue.colors[1]} mt={2}>
                  {t('generating.report')}
                </Typography>
                <Typography variant="body1" mb={2}>
                  {t('wait')}
                </Typography>
                <LinearProgress
                  sx={{
                    backgroundColor: fsColors.fsDarkBlue.colors[3],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: fsColors.fsDarkBlue.colors[1],
                    },
                    height: 5,
                    width: 350,
                    mb: 3,
                  }}
                />
              </>
            ) : generationStatus === 'success' ? (
              <>
                <motion.div
                  className="report-outline"
                  initial={{ y: '100%', opacity: 0 }}
                  animate={outlineControls}
                >
                  <ReportOutline />
                </motion.div>

                <motion.div
                  className="report-header"
                  initial={{ x: '-100%', opacity: 0 }}
                  animate={headerControls}
                >
                  <ReportHeader />
                </motion.div>

                {[0, 1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    className={`report-body${i + 1}`}
                    initial={{ x: '-100%', opacity: 0 }}
                    animate={bodyControls[i]}
                  >
                    <ReportBody />
                  </motion.div>
                ))}

                <motion.div
                  className="report-success"
                  initial={{ opacity: 0, rotate: 0 }}
                  animate={successControls}
                >
                  <ReportSuccess />
                </motion.div>
                <FairManSuccess />
                <Typography variant="h6" color={fsColors.fsDarkBlue.colors[1]}>
                  {t('report.generated')}
                </Typography>
                <Typography variant="body1">{t('report.generated.actions')}</Typography>
              </>
            ) : generationStatus === 'failed' ? (
              <>
                <FairManUnsuccess className="fair-man-unsuccess" />
                <ReportOutline className="report-outline" />
                <motion.div
                  className="report-failed"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={failedControls}
                >
                  <ReportError />
                </motion.div>
                <Typography variant="h6" color="primary.main">
                  {t('something.went.wrong.small')}
                </Typography>
                <Typography>{t('something.went.wrong')}</Typography>
              </>
            ) : null}
          </Stack>
          {generationStatus === 'success' && (
            <Button
              variant="contained"
              sx={{
                mt: 2,
                mr: 2,
                mb: 2,
              }}
              onClick={() => {
                dispatch(setReports())
                onClose(false)
              }}
            >
              {t('goto.report')}
            </Button>
          )}
          {generationStatus === 'failed' && (
            <Button
              variant="outlined"
              sx={{
                mt: 2,
                mr: 2,
                mb: 2,
              }}
              onClick={() => {
                onClose(true)
              }}
            >
              {t('close')}
            </Button>
          )}
        </Stack>
      </Box>
    </Modal>
  )
}

export default FsReportSpinner
